import React, { useEffect, useState } from 'react'
import { Paper, Button } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getResumen, notificarPendientes } from 'src/services/preciosService'
import { getPermissions } from 'src/utils/Common'
import { permitsList } from 'src/constants/permits'
import { PRECIOS_LISTA } from 'src/routes/paths'

/**
 * Tarjeta de indicador. `pendiente` marca las que significan "hay trabajo por hacer": cuando el número
 * es mayor a cero la tarjeta se resalta sola, en vez de repetir el dato en una alerta aparte debajo.
 */
const Kpi = ({ n, label, color, onClick, pendiente = false }) => {
  const hayTrabajo = pendiente && Number(n || 0) > 0
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      className="precios-kpi"
      style={{
        position: 'relative',
        padding: '12px 16px 12px 18px',
        minWidth: 138,
        borderRadius: 10,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        borderColor: hayTrabajo ? color : undefined,
        background: hayTrabajo ? '#fffdf7' : undefined,
      }}
      title={onClick ? 'Ver detalle' : undefined}
    >
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: hayTrabajo ? 5 : 4, background: color }} />
      <div style={{ fontSize: 26, fontWeight: 800, color: hayTrabajo ? color : '#0f2439', lineHeight: 1.05 }}>
        {n ?? 0}
      </div>
      <div style={{ fontSize: 10.5, color: '#6b7684', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 3 }}>
        {label}
      </div>
    </Paper>
  )
}

const num = (v) => Number(v || 0)

const isoLocal = (d) => {
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}
const hoy = new Date()
const primerDiaMes = isoLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1))
const hoyStr = isoLocal(hoy)

const ResumenBanner = ({ onIr, refreshKey, desde: desdeProp, hasta: hastaProp }) => {
  const [r, setR] = useState(null)
  // fecha gobernada por el selector global del contenedor; fallback = mes actual (uso standalone)
  const desde = desdeProp ?? primerDiaMes
  const hasta = hastaProp ?? hoyStr
  const history = useHistory()
  const permisos = getPermissions() || {}
  const isAdmin = Boolean(permisos[permitsList.PRECIOS_ADMIN])

  const notificar = async () => {
    const res = await notificarPendientes()
    if (res && res.success && res.ok) {
      toast.success(`${res.modo === 'prueba' ? 'Resumen de prueba enviado (solo al correo de prueba)' : 'Resumen enviado'}. Canales con pendientes: ${res.canales_con_pendientes}.`)
    } else {
      toast.info('Sin pendientes que notificar.')
    }
  }

  useEffect(() => {
    getResumen(desde, hasta).then((res) => {
      if (res && res.success) setR(res.data)
    })
    // refreshKey cambia al navegar entre tabs -> los KPIs reflejan acciones recién hechas
  }, [desde, hasta, refreshKey])

  // dest: clave de tab ('back','sync','promos','precios') o ruta ('/...')
  const go = (dest) => {
    if (!dest) return
    if (dest.startsWith('/')) history.push(dest)
    else if (onIr) onIr(dest)
  }

  // Solo lo que NO está ya en una tarjeta. "Pendientes Back" y "Por publicar" salían dos veces
  // (tarjeta + alerta) y eran el mismo número: ahora la tarjeta se pinta sola cuando hay trabajo.
  const avisos = []
  if (r) {
    if (num(r.sync_errores) > 0)
      avisos.push({ tono: 'error', txt: `${r.sync_errores} sincronización(es) con error`, dest: 'sync' })
    if (num(r.sin_ficha) > 0)
      avisos.push({ tono: 'warn', txt: `${r.sin_ficha} sin ficha en ecommerce`, dest: 'sync' })
    if (num(r.bonos_por_vencer) > 0)
      avisos.push({ tono: 'info', txt: `${r.bonos_por_vencer} bono(s) vencen en 2 días`, dest: 'promos' })
    if (num(r.sin_notificar) > 0)
      avisos.push({ tono: 'info', txt: `${r.sin_notificar} precio(s) sin notificar a socios`, dest: PRECIOS_LISTA })
  }

  const TONOS = {
    error: { bg: '#fdecec', bd: '#f3b6b6', fg: '#8f2d2d' },
    warn: { bg: '#fff4dd', bd: '#f3d089', fg: '#7a5300' },
    info: { bg: '#eaf2fb', bd: '#b9d3f0', fg: '#1c4a80' },
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <style>{`.precios-kpi{transition:box-shadow .15s ease,border-color .15s ease,transform .15s ease}
.precios-kpi:hover{box-shadow:0 3px 12px rgba(15,36,57,.10);transform:translateY(-1px)}`}</style>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={notificar}>
            Notificar pendientes a responsables
          </Button>
        </div>
      )}

      {r && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: avisos.length ? 10 : 0 }}>
            <Kpi n={r.materiales} label="Materiales" color="#64748b" onClick={() => go('trade')} />
            <Kpi n={r.cambios_periodo} label="Cambios (periodo)" color="#2563eb" onClick={() => go('trade')} />
            <Kpi n={r.back_pendientes} label="Pendientes Back" color="#f59e0b" onClick={() => go('back')} pendiente />
            <Kpi n={r.back_completos} label="Por publicar" color="#6366f1" onClick={() => go('back')} pendiente />
            <Kpi n={r.sync_pendientes} label="Sync pendientes" color="#0ea5e9" onClick={() => go('sync')} pendiente />
            <Kpi n={r.bonos_vigentes} label="Bonos vigentes" color="#16a34a" onClick={() => go('promos')} />
            <Kpi n={r.envios_periodo} label="Envíos (periodo)" color="#0d9488" onClick={() => go(PRECIOS_LISTA)} />
          </div>

          {/* Una sola línea, no una pila de bloques: solo lo que no está en las tarjetas */}
          {avisos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Requiere atención
              </span>
              {avisos.map((a) => {
                const t = TONOS[a.tono]
                return (
                  <button
                    type="button"
                    key={a.txt}
                    onClick={() => go(a.dest)}
                    style={{
                      background: t.bg,
                      border: `1px solid ${t.bd}`,
                      color: t.fg,
                      borderRadius: 20,
                      padding: '3px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {a.txt}
                  </button>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ResumenBanner
